const dateFormat = require('dateformat')

/**
 * This is just the date. No time zone. No time.
 * Its like Date but everything is in UTC time.
 * Certain differences like setting date to be < 1 or
 *      > the last day in the month will have unexpected behaviour.
 *      Same goes for months in a year not in [0, 11]
 * Years Range:     [0, 2**21 / (12*31)]
 * Months Range:    [0, 11]
 * Days Range:      [1, Last Day of month]
 *
 * Warning using toDateTime will convert it to UTC time
 *      This could cause issues if its at the final 7pm EST
 *
 * Decide to make new Just(null) to be equivalent to new Just(undefined)
 */
class JustDate {
    /**
     * @param {json} date can also be year
     * @param {number} month
     * @param {json} day
     */
    constructor(date, month, day) {
        if (date === undefined && arguments.length === 1) {
            this.year = Number.NaN
            this.month = Number.NaN
            this.date = Number.NaN
            return
        }
        if (date === null) {
            date = new Date(null)
        }

        if (date && typeof date === 'object') {
            if (date.hasOwnProperty('year') &&
                date.hasOwnProperty('month') &&
                date.hasOwnProperty('date')) {
                this.year = date.year
                this.month = date.month
                this.date = date.date
                return
            }
        }

        if (typeof date === typeof month &&
            typeof month === typeof day &&
            typeof day === 'number') {
            this.year = date
            this.month = month
            this.date = day-1
            return
        }

        if (date === undefined) {
            date = new Date()
            this.year = date.getFullYear()
            this.month = date.getMonth()
            this.date = date.getDate()-1
            return
        }


        if (!date || date instanceof Date || typeof date === 'string') {
            if (!date) {
                date = new Date()
                date.setUTCMinutes(date.getMinutes())
            } else if (typeof date === 'string') {
                date = new Date(date)
            }
            this.year = date.getUTCFullYear()
            this.month = date.getUTCMonth()
            this.date = date.getUTCDate()-1
            return
        }

        if (typeof date === 'number') {
            this.date = date % 31
            date = Math.floor(date / 31)
            this.month = date % 12
            date = Math.floor(date / 12)
            this.year = date
            return
        }

        this.year = 0
        this.month = 0
        this.date = 0
    }

    /**
     * Create a new JustDate from json
     * @param {json} json
     * @return {JustDate?}
     */
    static from(json) {
        return new JustDate(json)
    }


    /**
     * Returns ((year * 12) + month) * 31 + date
     * @return {number}
     */
    toSingleNumber() {
        return ((this.year*12) + this.month) * 31 + this.date
    }

    /**
     * Return JSON.stringify version of this
     * @return {this.number}
     */
    toJSON() {
        // return this.toSingleNumber()
        let month = '' + (this.month+1)
        if (month.length === 1) {
            month = '0' + month
        }

        let date = '' + (this.date+1)
        if (date.length === 1) {
            date = '0' + date
        }

        return '' + this.year + '-' + month + '-' + date
    }

    /**
     * Return a Date object
     * @note not sure if there is a special way to put
     *          in the values of the dates
     *          but doing it in smallest to largest values
     *          causes errors.
     * @return {Date}
     */
    toDateTime() {
        let minutes = (new Date()).getTimezoneOffset()
        if (minutes < 0) {
            minutes += 60*24
        }

        const d = new Date(this.year, this.month, this.date+1, 0, minutes, 0)
        d.setUTCFullYear(this.year)
        d.setUTCMonth(this.month)
        d.setUTCDate(this.date+1)
        d.setUTCMinutes(minutes)
        d.setUTCDate(this.date+1)
        d.setUTCMonth(this.month)
        d.setUTCFullYear(this.year)
        return d
    }

    /**
     * @return {Date}
     */
    toDate() {
        return new Date(this.year, this.month, this.date+1)
    }


    /**
     * Returns 0 iff it is not an actual date past UNIX EPOCH
     * @return {number} this.toSingleNumber()
     */
    valueOf() {
        if (
            this.year === null || this.year === Number.NaN ||
            (this.year <= 1970 && this.month <= 0 && this.date <= 0)
        ) {
            return 0
        }
        return this.toSingleNumber()
    }

    /**
     * @param {text} format
     * @return {text}
     */
    formatDate(format) {
        const d = new Date()
        d.setFullYear(this.year)
        d.setMonth(this.month)
        d.setDate(this.date+1)
        return dateFormat(d, format)
    }


    // MARK: Basic Setters

    /**
     * This will set the year
     * @param {number} year
     */
    setYear(year) {
        if (year >= 0 && year < 100) {
            year += 1900
        }
        this.year = year
        return this
    }

    /**
     * This will set the year
     * @param {number} year
     */
    setFullYear(year) {
        this.year = year
        return this
    }

    /**
     * This will set the month
     * @param {number} month
     */
    setMonth(month) {
        this.month = month
        return this
    }

    /**
     * This will set the date
     * @param {number} date
     */
    setDate(date) {
        this.date = date-1
        return this
    }

    // MARK: Basic Getters

    /**
     * This will get the year
     * @return {number} year
     */
    getYear() {
        return this.year-1900
    }

    /**
     * This will get the full year
     * @return {number} year
     */
    getFullYear() {
        return this.year
    }

    /**
     * This will get the month
     * @return {number} month
     */
    getMonth() {
        return this.month
    }

    /**
     * This will get the date
     * @return {number} date
     */
    getDate() {
        return this.date+1
    }

    /**
     * @param {string} format
     * @return {string}
     */
    toString(format='mmmm d, yyyy') {
        return dateFormat(this.toDateTime(), format)
    }

    /**
     * @param {number} by
     * @return {JustDate} advanced by
     *      the number of days specified.
     */
    advanceDate(by=30) {
        const date = this.toDate()
        date.setDate(date.getDate()+by)
        return new JustDate(date)
    }

    /**
     * @param {JustDate} other
     * @return {number} total days
     */
    differanceDays(other) {
        const lhs = this.toDate()
        const rhs = other.toDate()

        return Math.round((lhs-rhs)/(60*60*24*1000))
    }
}

module.exports = {
    JustDate,
}
